import { useState } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { AlertCircle, ArrowLeft, RefreshCw } from 'lucide-react'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { LoadingSpinner } from '@/components/loading-spinner'
import { useAuth } from '@/lib/auth-oidc'
import { useSearchStats, useVehicleStats } from '@/lib/api'
import { useFeatureFlag } from '@/lib/use-feature-flags'
import { localizeDigits } from '@/lib/localize-digits'
import { cn } from '@/lib/utils'

export const Route = createFileRoute('/settings/analytics')({
  component: AnalyticsPage,
})

const TIME_RANGES = [
  { value: '7', labelKey: 'analytics.timeRange.7days' },
  { value: '30', labelKey: 'analytics.timeRange.30days' },
  { value: '90', labelKey: 'analytics.timeRange.90days' },
] as const

const DOW_LABELS: Record<string, string> = {
  '1': 'Sun',
  '2': 'Mon',
  '3': 'Tue',
  '4': 'Wed',
  '5': 'Thu',
  '6': 'Fri',
  '7': 'Sat',
}

function StatCard({
  label,
  value,
  subValue,
}: {
  label: string
  value: string
  subValue?: string
}) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-2xl font-bold mt-1">{value}</p>
        {subValue && (
          <p className="text-xs text-muted-foreground mt-0.5">{subValue}</p>
        )}
      </CardContent>
    </Card>
  )
}

function BarChart({
  data,
  labels,
  title,
  description,
}: {
  data: Record<string, number>
  labels?: Record<string, string>
  title: string
  description: string
}) {
  const entries = Object.entries(data)
  if (entries.length === 0) return null

  const maxValue = Math.max(...entries.map(([, v]) => v), 1)

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>{description}</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-end gap-1 h-32">
          {entries.map(([key, value]) => {
            const height = (value / maxValue) * 100
            const label = labels?.[key] ?? key
            return (
              <div
                key={key}
                className="flex-1 flex flex-col items-center gap-1"
              >
                <span className="text-[10px] text-muted-foreground">
                  {localizeDigits(String(value))}
                </span>
                <div
                  className={cn(
                    'w-full rounded-t bg-primary/80 transition-all min-h-[2px]',
                  )}
                  style={{ height: `${Math.max(height, 2)}%` }}
                />
                <span className="text-[10px] text-muted-foreground truncate max-w-full">
                  {label}
                </span>
              </div>
            )
          })}
        </div>
      </CardContent>
    </Card>
  )
}

function AnalyticsPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [analyticsEnabled] = useFeatureFlag('analytics')
  const [days, setDays] = useState(30)
  const [selectedAccount, setSelectedAccount] = useState<string | undefined>(
    undefined,
  )

  const {
    data: searchStats,
    isLoading: searchLoading,
    error: searchError,
    refetch: refetchSearch,
    isFetching: searchFetching,
  } = useSearchStats(days, selectedAccount, analyticsEnabled)

  const {
    data: vehicleStats,
    isLoading: vehicleLoading,
    error: vehicleError,
    refetch: refetchVehicle,
    isFetching: vehicleFetching,
  } = useVehicleStats(days, selectedAccount, analyticsEnabled)

  const isLoading = searchLoading || vehicleLoading
  const isFetching = searchFetching || vehicleFetching
  const error = searchError || vehicleError

  const handleRefresh = () => {
    refetchSearch()
    refetchVehicle()
  }

  if (!analyticsEnabled) {
    navigate({ to: '/settings' })
    return null
  }

  // Build hour labels (0-23)
  const hourData: Record<string, number> = {}
  if (searchStats?.searches_by_hour) {
    for (let h = 0; h < 24; h++) {
      hourData[String(h)] = searchStats.searches_by_hour[String(h)] ?? 0
    }
  }

  // Build DOW data
  const dowData: Record<string, number> = {}
  if (searchStats?.searches_by_dow) {
    for (let d = 1; d <= 7; d++) {
      dowData[String(d)] = searchStats.searches_by_dow[String(d)] ?? 0
    }
  }

  const accessibleAccounts = user?.accessible_accounts ?? []

  return (
    <div>
      {/* Fixed Header */}
      <div className="fixed-header">
        <div className="container mx-auto max-w-screen-md px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Link to="/settings">
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <ArrowLeft className="h-4 w-4" />
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold">{t('analytics.title')}</h1>
                <p className="text-xs text-muted-foreground">
                  {t('analytics.description')}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="icon"
              onClick={handleRefresh}
              disabled={isFetching}
              className="h-8 w-8"
            >
              <RefreshCw
                className={cn('h-4 w-4', isFetching && 'animate-spin')}
              />
            </Button>
          </div>
        </div>
      </div>

      {/* Scrollable Content */}
      <div className="container mx-auto max-w-screen-md px-4 content-with-fixed-header pb-nav-and-safe-area">
        <div className="space-y-4 pt-4">
          {/* Filters Row */}
          <div className="flex gap-3">
            <Select
              value={String(days)}
              onValueChange={(v) => setDays(Number(v))}
            >
              <SelectTrigger className="w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIME_RANGES.map((range) => (
                  <SelectItem key={range.value} value={range.value}>
                    {t(range.labelKey)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {accessibleAccounts.length > 1 && (
              <Select
                value={selectedAccount ?? 'all'}
                onValueChange={(v) =>
                  setSelectedAccount(v === 'all' ? undefined : v)
                }
              >
                <SelectTrigger className="flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">
                    {t('analytics.allAccounts')}
                  </SelectItem>
                  {accessibleAccounts.map((account) => (
                    <SelectItem key={account} value={account}>
                      {account}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </div>

          {/* Loading */}
          {isLoading && (
            <Card>
              <CardContent className="py-12">
                <LoadingSpinner label={t('analytics.loading')} />
              </CardContent>
            </Card>
          )}

          {/* Error */}
          {error && !isLoading && (
            <Card>
              <CardContent className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-3 text-center">
                  <AlertCircle className="h-8 w-8 text-destructive" />
                  <p className="font-medium text-destructive">
                    {t('analytics.error')}
                  </p>
                  <Button onClick={handleRefresh} variant="outline" size="sm">
                    <RefreshCw className="h-4 w-4 me-2" />
                    {t('common.retry')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Stats Cards */}
          {!isLoading && searchStats && vehicleStats && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <StatCard
                  label={t('analytics.stats.totalSearches')}
                  value={localizeDigits(
                    String(searchStats.total_searches ?? 0),
                  )}
                  subValue={`${localizeDigits(String(searchStats.success_rate ?? 0))}% ${t('analytics.stats.successRate')}`}
                />
                <StatCard
                  label={t('analytics.stats.avgDuration')}
                  value={
                    searchStats.avg_duration_ms != null
                      ? localizeDigits(
                          `${Math.round(searchStats.avg_duration_ms)}ms`,
                        )
                      : '--'
                  }
                />
                <StatCard
                  label={t('analytics.stats.totalBookings')}
                  value={localizeDigits(
                    String(vehicleStats.total_bookings ?? 0),
                  )}
                  subValue={`${localizeDigits(String(vehicleStats.successful_bookings ?? 0))} ${t('analytics.stats.successful')}`}
                />
                <StatCard
                  label={t('analytics.stats.totalRequests')}
                  value={localizeDigits(
                    String(vehicleStats.total_requests ?? 0),
                  )}
                  subValue={`${localizeDigits(String(vehicleStats.unique_accounts ?? 0))} ${t('analytics.stats.activeAccounts')}`}
                />
              </div>

              {/* Searches by Hour */}
              {Object.keys(hourData).length > 0 && (
                <BarChart
                  data={hourData}
                  title={t('analytics.charts.byHour')}
                  description={t('analytics.charts.byHourDesc')}
                />
              )}

              {/* Searches by Day of Week */}
              {Object.keys(dowData).length > 0 && (
                <BarChart
                  data={dowData}
                  labels={DOW_LABELS}
                  title={t('analytics.charts.byDow')}
                  description={t('analytics.charts.byDowDesc')}
                />
              )}

              {/* Top API Actions */}
              {(vehicleStats.top_actions?.length ?? 0) > 0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {t('analytics.charts.topActions')}
                    </CardTitle>
                    <CardDescription>
                      {t('analytics.charts.topActionsDesc')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {vehicleStats.top_actions?.map((action, idx) => {
                        const maxCount =
                          vehicleStats.top_actions?.[0]?.count ?? 1
                        const pct = (action.count / maxCount) * 100
                        return (
                          <div key={idx} className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="truncate text-muted-foreground">
                                {action.action}
                              </span>
                              <span className="font-medium ms-2">
                                {localizeDigits(String(action.count))}
                              </span>
                            </div>
                            <div className="h-1.5 w-full rounded bg-muted">
                              <div
                                className="h-full rounded bg-primary/70"
                                style={{ width: `${pct}%` }}
                              />
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Response Status Breakdown */}
              {Object.keys(vehicleStats.requests_by_status ?? {}).length >
                0 && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-base">
                      {t('analytics.charts.responseStatus')}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="flex gap-4 flex-wrap">
                      {Object.entries(
                        vehicleStats.requests_by_status ?? {},
                      ).map(([category, count]) => (
                        <div key={category} className="text-center">
                          <p className="text-lg font-bold">
                            {localizeDigits(String(count))}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {t(`analytics.status.${category}`, category)}
                          </p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}
